// Строка поля карточки — как в левой колонке Bitrix: подпись сверху мелким,
// значение крупнее, пустое — «не заполнено» серым, правка — по «Править» у строки.
//
// Поле описывается ДАННЫМИ (CardField), а не разметкой: одна и та же строка рисует
// текст, число, дату, список, галочку и ссылку — по типу. Компонент ничего не знает
// про сделку или ядро: сохранение отдаётся наружу (onSave), и пока оно не разрешилось,
// строка ждёт. Ошибка сохранения показывается у самого поля, а не глотается.
//
// Зеркальные поля Bitrix (origin: 'bitrix') помечаются капсулой и НЕ редактируются
// ни при каком onSave: их правят в Bitrix, и кнопка правки здесь была бы обещанием,
// которое выполняет чужая система.
import { useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'

import { fmtDate, fmtText } from '../../lib/format'
import './card.css'

export type FieldType = 'text' | 'number' | 'date' | 'select' | 'checkbox' | 'link'

export interface CardField {
  /** Код поля — с ним поле уйдёт в onSave (например, TITLE). */
  code: string
  label: string
  type: FieldType
  /** Сырое значение (то, что редактируется). */
  value: unknown
  /** Готовый вид значения (ссылка, значок). Без него значение рисуется по типу. */
  display?: ReactNode
  /** Для type='select'. */
  options?: Array<{ id: string; name: string }>
  readOnly?: boolean
  /** Почему нельзя писать — подсказка на пометке. */
  readOnlyReason?: string
  /** 'bitrix' — зеркальное поле (пометка, без правки); 'crm' — своё. */
  origin?: 'crm' | 'bitrix'
  /** Для type='link' без display: значение станет ссылкой сюда. */
  href?: string
  /** title у значения (например, почему дата пуста). */
  hint?: string
}

interface Props {
  field: CardField
  /**
   * Сохранить новое значение. Для checkbox приходит boolean, для остальных — строка.
   * Бросил ошибку — строка покажет её и останется в правке.
   */
  onSave?: (code: string, value: unknown) => Promise<void> | void
}

const пусто = (v: unknown) => v === null || v === undefined || v === ''

/** Значение для поля ввода: черновик правки начинается с текущего значения. */
function вЧерновик(f: CardField): string {
  if (f.type === 'date') {
    // input[type=date] понимает только «ГГГГ-ММ-ДД»
    const s = String(f.value ?? '')
    return s.slice(0, 10)
  }
  return пусто(f.value) ? '' : String(f.value)
}

function видПоТипу(f: CardField): ReactNode {
  if (f.type === 'checkbox') return f.value === true || f.value === 'Y' ? 'да' : 'нет'
  if (f.type === 'date') return fmtDate(String(f.value ?? ''))
  if (f.type === 'select') {
    const тек = f.options?.find((o) => o.id === String(f.value ?? ''))
    return тек ? тек.name : fmtText(f.value)
  }
  if (f.type === 'link' && f.href) return <a href={f.href}>{fmtText(f.value)}</a>
  return fmtText(f.value)
}

export function FieldRow({ field, onSave }: Props) {
  const [правка, setПравка] = useState(false)
  const [черновик, setЧерновик] = useState('')
  const [галочка, setГалочка] = useState(false)
  const [ждём, setЖдём] = useState(false)
  const [беда, setБеда] = useState('')

  const зеркальное = field.origin === 'bitrix'
  const можноПравить = !!onSave && !field.readOnly && !зеркальное

  const начать = () => {
    setЧерновик(вЧерновик(field))
    setГалочка(field.value === true || field.value === 'Y')
    setБеда('')
    setПравка(true)
  }

  const отмена = () => {
    setПравка(false)
    setБеда('')
  }

  const сохранить = async () => {
    if (!onSave) return
    const новое: unknown = field.type === 'checkbox' ? галочка : черновик
    // Ничего не поменялось — это отмена, а не запись: лишнее поле в запросе — риск
    // задеть то, на чём висит бизнес-процесс.
    const старое: unknown = field.type === 'checkbox'
      ? (field.value === true || field.value === 'Y')
      : вЧерновик(field)
    if (новое === старое) { отмена(); return }
    setЖдём(true)
    setБеда('')
    try {
      await onSave(field.code, новое)
      setПравка(false)
    } catch (e) {
      setБеда(e instanceof Error ? e.message : String(e))
    } finally {
      setЖдём(false)
    }
  }

  const поКлавише = (e: KeyboardEvent) => {
    if (e.key === 'Escape') отмена()
    if (e.key === 'Enter' && field.type !== 'checkbox') void сохранить()
  }

  const метка = (
    <div className="frow-label">
      <span>{field.label}</span>
      {зеркальное && (
        <span
          className="origin-mark"
          title={field.readOnlyReason
            || 'Зеркальное поле Bitrix: показывается как есть, правится в Bitrix'}
        >
          Bitrix
        </span>
      )}
    </div>
  )

  if (правка && можноПравить) {
    return (
      <div className="frow">
        {метка}
        <div className="frow-form" onKeyDown={поКлавише}>
          {field.type === 'select' ? (
            <select value={черновик} aria-label={field.label}
                    onChange={(e) => setЧерновик(e.target.value)}>
              {/* значение вне справочника не теряем из списка */}
              {черновик && !(field.options ?? []).some((o) => o.id === черновик) && (
                <option value={черновик}>{черновик}</option>
              )}
              {(field.options ?? []).map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          ) : field.type === 'checkbox' ? (
            <input type="checkbox" checked={галочка} aria-label={field.label}
                   onChange={(e) => setГалочка(e.target.checked)} />
          ) : (
            <input
              type={field.type === 'date' ? 'date' : 'text'}
              inputMode={field.type === 'number' ? 'decimal' : undefined}
              value={черновик}
              aria-label={field.label}
              onChange={(e) => setЧерновик(e.target.value)}
            />
          )}
          <button type="button" className="primary" onClick={() => void сохранить()}
                  disabled={ждём}>
            {ждём ? 'Сохраняю…' : 'Сохранить'}
          </button>
          <button type="button" onClick={отмена} disabled={ждём}>Отмена</button>
        </div>
        {беда && <div className="error">{беда}</div>}
      </div>
    )
  }

  return (
    <div className="frow">
      {метка}
      <div className="frow-value" title={field.hint}>
        {пусто(field.value) && field.display === undefined
          ? <span className="frow-empty">не заполнено</span>
          : (field.display ?? видПоТипу(field))}
        {можноПравить && (
          <button type="button" className="frow-edit" onClick={начать}>Править</button>
        )}
      </div>
    </div>
  )
}
